import React, { useState, useEffect, useCallback, memo } from 'react';
import { FiPlus, FiTag, FiChevronLeft, FiMessageSquare, FiInbox } from 'react-icons/fi';
import { useTicketStore } from '../../store/ticketStore';
import { useShallowStore } from '../../hooks/useShallowStore';
import api from '../../services/api';
import VirtualizedList from '../common/VirtualizedList.jsx';
import { SkeletonLine, SkeletonBlock, SkeletonCircle } from '../common/Skeleton.jsx';
import './Tickets.css';

const STATUS_LABELS = {
  open: 'Open', in_progress: 'In Progress', ready_for_testing: 'Ready for Testing',
  ready_to_deploy: 'Ready to Deploy', deployed: 'Deployed', resolved: 'Resolved', closed: 'Closed'
};
const PRIORITY_COLORS = { low: '#28a745', medium: '#ffc107', high: '#fd7e14', critical: '#dc3545' };
const CLIENT_TICKET_ROW_HEIGHT = 56;

const clientTicketDetailSelector = (state) => ({
  fetchTicket: state.fetchTicket,
  currentTicket: state.currentTicket,
  addComment: state.addComment,
  loading: state.loading,
});

const clientTicketsListSelector = (state) => ({
  tickets: state.tickets,
  fetchTickets: state.fetchTickets,
  loading: state.loading,
});

const createTicketSelector = (state) => ({ createTicket: state.createTicket });

// Mirrors client-ticket-row-grid's 7 columns so the skeleton lines up with
// the real rows once data arrives — no layout shift on load.
const ClientTicketRowSkeleton = memo(function ClientTicketRowSkeleton() {
  return (
    <div className="ticket-row client-ticket-row-grid" aria-hidden="true">
      <div className="ticket-cell"><SkeletonBlock width={64} height={18} radius={4} /></div>
      <div className="ticket-cell"><SkeletonLine width="80%" height={13} /></div>
      <div className="ticket-cell"><SkeletonLine width="70%" height={13} /></div>
      <div className="ticket-cell"><SkeletonBlock width={64} height={20} radius={12} /></div>
      <div className="ticket-cell"><SkeletonBlock width={90} height={20} radius={12} /></div>
      <div className="ticket-cell"><SkeletonLine width={70} height={13} /></div>
      <div className="ticket-cell"><SkeletonLine width={70} height={13} /></div>
    </div>
  );
});

const ClientTicketsTableSkeleton = memo(function ClientTicketsTableSkeleton({ rows = 8 }) {
  return (
    <div className="tickets-table-wrapper" aria-hidden="true">
      <div className="tickets-table-head client-ticket-row-grid" role="row">
        <div className="ticket-header-cell">Ticket #</div>
        <div className="ticket-header-cell">Title</div>
        <div className="ticket-header-cell">Project</div>
        <div className="ticket-header-cell">Priority</div>
        <div className="ticket-header-cell">Status</div>
        <div className="ticket-header-cell">Created</div>
        <div className="ticket-header-cell">Last Updated</div>
      </div>
      <div className="tickets-table-body">
        {Array.from({ length: rows }).map((_, i) => <ClientTicketRowSkeleton key={i} />)}
      </div>
    </div>
  );
});

// Mirrors ClientTicketDetail's layout (header badge/title/meta, description,
// comments, sidebar info card) so the loading state doesn't jump around.
const ClientTicketDetailSkeleton = memo(function ClientTicketDetailSkeleton() {
  return (
    <div className="ticket-detail" aria-hidden="true">
      <div className="ticket-detail-header">
        <SkeletonBlock width={90} height={22} radius={6} style={{ marginBottom: 10 }} />
        <SkeletonLine width="45%" height={22} style={{ marginBottom: 12 }} />
        <div className="ticket-meta-row">
          <SkeletonBlock width={70} height={22} radius={12} />
          <SkeletonBlock width={100} height={22} radius={12} />
        </div>
      </div>

      <div className="ticket-detail-body">
        <div className="ticket-main">
          <section className="ticket-section">
            <SkeletonLine width={110} height={14} style={{ marginBottom: 14 }} />
            <SkeletonLine width="95%" height={12} style={{ marginBottom: 8 }} />
            <SkeletonLine width="80%" height={12} />
          </section>

          <section className="ticket-section">
            <SkeletonLine width={130} height={14} style={{ marginBottom: 14 }} />
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <SkeletonCircle size={28} />
                <SkeletonLine width="60%" height={12} />
              </div>
            ))}
          </section>
        </div>

        <div className="ticket-sidebar">
          <div className="ticket-info-card">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="info-row">
                <SkeletonLine width={70} height={11} />
                <SkeletonLine width={90} height={11} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Single row in the (virtualized) client tickets table. Memoized so
 * re-renders of the parent list only re-render rows whose `ticket`
 * reference actually changed. Column set differs from the admin
 * TicketRow (no client/assignee columns, adds "Last Updated"), so it
 * is kept as its own component rather than reused.
 */
const ClientTicketRow = React.memo(function ClientTicketRow({ ticket, style, onSelect }) {
  const handleClick = () => onSelect(ticket._id);
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(ticket._id);
    }
  };

  return (
    <div
      className="ticket-row client-ticket-row-grid"
      style={style}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="row"
      tabIndex={0}
    >
      <div className="ticket-cell" role="cell"><span className="ticket-num">{ticket.ticketNumber}</span></div>
      <div className="ticket-cell" role="cell">{ticket.title}</div>
      <div className="ticket-cell" role="cell">{ticket.project?.name}</div>
      <div className="ticket-cell" role="cell">
        <span className={`priority-badge priority-${ticket.priority}`}>{ticket.priority}</span>
      </div>
      <div className="ticket-cell" role="cell">
        <span className={`status-badge status-${ticket.status}`}>{STATUS_LABELS[ticket.status]}</span>
      </div>
      <div className="ticket-cell" role="cell">{new Date(ticket.createdAt).toLocaleDateString()}</div>
      <div className="ticket-cell" role="cell">{new Date(ticket.updatedAt).toLocaleDateString()}</div>
    </div>
  );
});

function ClientTicketDetail({ ticketId, onBack }) {
  const { fetchTicket, currentTicket, addComment, loading } = useShallowStore(useTicketStore, clientTicketDetailSelector);
  const [comment, setComment] = useState('');

  useEffect(() => { fetchTicket(ticketId); }, [ticketId, fetchTicket]);

  const handleComment = useCallback(async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    await addComment(ticketId, comment);
    setComment('');
  }, [addComment, ticketId, comment]);

  if (loading || !currentTicket) return <ClientTicketDetailSkeleton />;

  const t = currentTicket;

  return (
    <div className="ticket-detail">
      <button className="back-btn" onClick={onBack}><FiChevronLeft /> My Tickets</button>

      <div className="ticket-detail-header">
        <div className="ticket-number-badge">{t.ticketNumber}</div>
        <h2>{t.title}</h2>
        <div className="ticket-meta-row">
          <span className={`priority-badge priority-${t.priority}`}>{t.priority}</span>
          <span className={`status-badge status-${t.status}`}>{STATUS_LABELS[t.status]}</span>
        </div>
      </div>

      <div className="ticket-detail-body">
        <div className="ticket-main">
          <section className="ticket-section">
            <h3>Description</h3>
            <p>{t.description || 'No description provided.'}</p>
          </section>

          <section className="ticket-section">
            <h3><FiMessageSquare /> Comments ({t.comments?.length || 0})</h3>
            <div className="comments-list">
              {t.comments?.length === 0 && <p className="empty-text">No comments yet.</p>}
              {t.comments?.map((c, i) => (
                <div key={c._id || i} className="comment-item">
                  <div className="comment-author">
                    <div className="comment-avatar">{c.author?.firstName?.[0]}{c.author?.lastName?.[0]}</div>
                    <span>{c.author?.firstName} {c.author?.lastName}</span>
                    <span className="comment-time">{new Date(c.createdAt).toLocaleString()}</span>
                  </div>
                  <p>{c.content}</p>
                </div>
              ))}
            </div>
            <form className="comment-form" onSubmit={handleComment}>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Add a reply..."
                rows={3}
              />
              <button type="submit" className="btn-primary" disabled={!comment.trim()}>Post Reply</button>
            </form>
          </section>

          <section className="ticket-section">
            <h3>Progress History</h3>
            <div className="activity-list">
              {t.activity?.map((a, i) => (
                <div key={a._id || i} className="activity-item">
                  <div className="activity-dot" />
                  <div className="activity-content">
                    <span className="activity-action">{a.action.replace(/_/g, ' ')}</span>
                    {a.detail && <span className="activity-detail"> — {a.detail}</span>}
                    <span className="activity-time">{new Date(a.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="ticket-sidebar">
          <div className="ticket-info-card">
            <div className="info-row"><span>Ticket #</span><strong>{t.ticketNumber}</strong></div>
            <div className="info-row"><span>Project</span><strong>{t.project?.name}</strong></div>
            <div className="info-row"><span>Status</span><strong>{STATUS_LABELS[t.status]}</strong></div>
            <div className="info-row"><span>Priority</span><strong style={{ color: PRIORITY_COLORS[t.priority] }}>{t.priority}</strong></div>
            <div className="info-row"><span>Assigned to</span><strong>{t.assignedTo ? `${t.assignedTo.firstName} ${t.assignedTo.lastName}` : 'Pending'}</strong></div>
            <div className="info-row"><span>Created</span><strong>{new Date(t.createdAt).toLocaleDateString()}</strong></div>
            <div className="info-row"><span>Last updated</span><strong>{new Date(t.updatedAt).toLocaleDateString()}</strong></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RaiseTicketModal({ onClose, onCreated }) {
  const { createTicket } = useShallowStore(useTicketStore, createTicketSelector);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', project: '', priority: 'medium' });

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.projects)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project) return;
    const ticket = await createTicket(form);
    if (ticket) { onCreated(); onClose(); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Raise a Ticket</h2></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Project *</label>
            <select value={form.project} onChange={e => setForm({ ...form, project: e.target.value })} required>
              <option value="">Select your project</option>
              {projects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="Brief description of the issue" />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={5} placeholder="Provide detailed information about the issue..." />
          </div>
          <div className="form-group">
            <label>Priority</label>
            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary">Submit Ticket</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ClientPortal() {
  const { tickets, fetchTickets, loading } = useShallowStore(useTicketStore, clientTicketsListSelector);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [showRaise, setShowRaise] = useState(false);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const selectTicket = useCallback((id) => setSelectedTicketId(id), []);
  const clientTicketRowKey = useCallback((t) => t._id, []);
  const renderClientTicketRow = useCallback((t, index, style) => (
    <ClientTicketRow ticket={t} style={style} onSelect={selectTicket} />
  ), [selectTicket]);

  if (selectedTicketId) {
    return <ClientTicketDetail ticketId={selectedTicketId} onBack={() => setSelectedTicketId(null)} />;
  }

  return (
    <div className="tickets-list-view">
      <div className="tickets-header">
        <h1><FiTag /> My Tickets</h1>
        <button className="btn-primary" onClick={() => setShowRaise(true)}>
          <FiPlus /> Raise Ticket
        </button>
      </div>

      {loading ? (
        <ClientTicketsTableSkeleton rows={8} />
      ) : tickets.length === 0 ? (
        <div className="tickets-empty">
          <FiInbox size={44} />
          <p>No tickets yet. Raise your first ticket!</p>
          <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => setShowRaise(true)}>
            <FiPlus /> Raise Ticket
          </button>
        </div>
      ) : (
        <div className="tickets-table-wrapper">
          <div className="tickets-table-head client-ticket-row-grid" role="row">
            <div className="ticket-header-cell" role="columnheader">Ticket #</div>
            <div className="ticket-header-cell" role="columnheader">Title</div>
            <div className="ticket-header-cell" role="columnheader">Project</div>
            <div className="ticket-header-cell" role="columnheader">Priority</div>
            <div className="ticket-header-cell" role="columnheader">Status</div>
            <div className="ticket-header-cell" role="columnheader">Created</div>
            <div className="ticket-header-cell" role="columnheader">Last Updated</div>
          </div>
          <div className="tickets-table-body" role="rowgroup">
            <VirtualizedList
              items={tickets}
              itemKey={clientTicketRowKey}
              rowHeight={CLIENT_TICKET_ROW_HEIGHT}
              renderRow={renderClientTicketRow}
              className="tickets-virtual-list scrollbar-thin"
              style={{ height: '100%' }}
            />
          </div>
        </div>
      )}

      {showRaise && (
        <RaiseTicketModal
          onClose={() => setShowRaise(false)}
          onCreated={() => fetchTickets()}
        />
      )}
    </div>
  );
}

export default ClientPortal;
